import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { VolatilitySuggestion, Watch } from '../api/types'
import { WATCHABLE_METRICS } from '../api/types'
import { Banner } from '../components/Banner'
import { ConfigList, ConfigRow, type RowChip } from '../components/ConfigList'
import { Drawer } from '../components/Drawer'
import { Funnels } from '../components/Funnels'
import { QuotaPanel } from '../components/QuotaPanel'
import { TrackedEvents } from '../components/TrackedEvents'
import { metricLabel } from '../format'

interface Props {
  propertyId?: string
}

/**
 * The Settings page's sections.
 *
 * Tabs rather than one scroll: watches grow a row each, so the page got long enough
 * that the quota panel and the tracked-event list — the two things people come here to
 * read — ended up a screen apart from each other.
 */
const SETTINGS_TABS = [
  { id: 'events', label: 'Tracked events' },
  { id: 'funnels', label: 'Funnels' },
  { id: 'watches', label: 'Watches' },
  { id: 'quota', label: 'GA4 quota' },
] as const

type SettingsTab = (typeof SETTINGS_TABS)[number]['id']

function blankWatch(index: number): Watch {
  return {
    id: `watch-${index}`,
    label: '',
    kind: 'metric',
    target: 'sessions',
    segment: null,
    drop_percent: 40,
    spike_percent: null,
    minimum: null,
    severity: 'warning',
    enabled: true,
    muted_until: null,
  }
}

/** Turn a label into a slug id, so ids stay readable without the user managing them. */
function slugify(label: string, fallback: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || fallback
}

/** The rules themselves, in one line. A watch with no rule can never fire, so say so. */
function summariseWatch(watch: Watch): string {
  const rules: string[] = []
  if (watch.drop_percent !== null) rules.push(`falls ${watch.drop_percent}%`)
  if (watch.spike_percent !== null) rules.push(`rises ${watch.spike_percent}%`)
  if (watch.minimum !== null) rules.push(`below ${watch.minimum}`)
  const scope = watch.segment ? ` on ${watch.segment}` : ''
  return rules.length === 0 ? 'No rule set — cannot fire' : `Alerts if it ${rules.join(', ')}${scope}`
}

export function Settings({ propertyId }: Props) {
  const [watches, setWatches] = useState<Watch[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tab, setTab] = useState<SettingsTab>('events')
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  const [suggestion, setSuggestion] = useState<VolatilitySuggestion | null>(null)
  const [suggesting, setSuggesting] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    api
      .watches({ propertyId }, controller.signal)
      .then(setWatches)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'Could not load watches.')
      })
    return () => controller.abort()
  }, [propertyId])

  function update(index: number, patch: Partial<Watch>) {
    setWatches((current) =>
      current
        ? current.map((item, position) => (position === index ? { ...item, ...patch } : item))
        : current,
    )
    setDirty(true)
    setNotice(null)
  }

  async function save() {
    if (!watches) return
    setSaving(true)
    setError(null)
    setNotice(null)

    // Ids are derived from labels here rather than exposed as a field; a stable,
    // readable id matters to the API but is noise in the UI.
    const prepared = watches.map((item, index) => ({
      ...item,
      id: slugify(item.label, `watch-${index + 1}`),
      label: item.label.trim(),
    }))

    try {
      setWatches(await api.saveWatches(prepared, { propertyId }))
      setDirty(false)
      setEditing(null)
      setNotice('Saved. The daily check will use these on its next run.')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save watches.')
    } finally {
      setSaving(false)
    }
  }

  async function suggest() {
    setSuggesting(true)
    setError(null)
    try {
      setSuggestion(await api.suggestThresholds(8, { propertyId }))
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not analyse history.')
    } finally {
      setSuggesting(false)
    }
  }

  function applySuggestions() {
    if (!suggestion || !watches) return
    const byMetric = new Map(suggestion.metrics.map((item) => [item.metric, item]))
    setWatches(
      watches.map((watch) => {
        const match = watch.kind === 'metric' ? byMetric.get(watch.target) : undefined
        if (!match?.suggested_drop_percent) return watch
        return {
          ...watch,
          drop_percent: watch.drop_percent === null ? null : match.suggested_drop_percent,
          spike_percent: watch.spike_percent === null ? null : match.suggested_spike_percent,
        }
      }),
    )
    setDirty(true)
    setNotice('Thresholds updated from history — review, then save.')
  }

  if (error && !watches) return <Banner tone="critical">{error}</Banner>
  if (!watches) return <p className="placeholder">Loading watches…</p>

  const open = editing === null ? undefined : watches[editing]

  return (
    <>
      <nav className="admin-tabs" aria-label="Settings sections">
        {SETTINGS_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-tabs__tab${tab === item.id ? ' admin-tabs__tab--active' : ''}`}
            aria-current={tab === item.id ? 'page' : undefined}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === 'events' && (
        <section className="section" style={{ marginTop: 0 }}>
          <div className="section__head">
            <h2 className="section__title">Tracked events</h2>
            <span className="section__note">Always reported in the briefing</span>
          </div>
          <TrackedEvents propertyId={propertyId} />
        </section>
      )}

      {tab === 'funnels' && (
        <section className="section" style={{ marginTop: 0 }}>
          <div className="section__head">
            <h2 className="section__title">Funnels</h2>
            <span className="section__note">Where along the path people stop</span>
          </div>
          <Funnels propertyId={propertyId} />
        </section>
      )}

      {tab === 'quota' && (
        <section className="section" style={{ marginTop: 0 }}>
          <div className="section__head">
            <h2 className="section__title">GA4 quota</h2>
            <span className="section__note">Reported by GA4, not estimated here</span>
          </div>
          <QuotaPanel propertyId={propertyId} />
        </section>
      )}

      {tab === 'watches' && (
        <>
          <section className="section" style={{ marginTop: 0 }}>
            <div className="section__head">
              <h2 className="section__title">Watches</h2>
              <span className="section__note">Alert when something breaks</span>
            </div>
          </section>

          <p className="section__note" style={{ marginTop: 0 }}>
            Watches drive both the daily check and what an agent is told. Each one compares a
            settled day with the same weekday a week earlier.
          </p>

          {error && <Banner tone="critical">{error}</Banner>}
          {notice && <Banner tone="info">{notice}</Banner>}

          <div className="controls" style={{ marginTop: 16 }}>
            <button type="button" className="button" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save watches'}
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => {
                setWatches([...watches, blankWatch(watches.length + 1)])
                setEditing(watches.length)
                setDirty(true)
              }}
            >
              Add watch
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={suggest}
              disabled={suggesting}
            >
              {suggesting ? 'Analysing…' : 'Suggest from history'}
            </button>
            {dirty && <span className="section__note">Unsaved changes</span>}
          </div>

          {suggestion && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 6 }}>Measured over {suggestion.days_analysed} days</h3>
              <p className="section__note" style={{ marginTop: 0 }}>
                How much this property normally moves week over week, on the same weekday.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Metric</th>
                      <th scope="col">Typical</th>
                      <th scope="col">Noisiest 10%</th>
                      <th scope="col">Suggested drop</th>
                      <th scope="col">Alerts/week</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestion.metrics.map((item) => (
                      <tr key={item.metric}>
                        <th scope="row">{metricLabel(item.metric)}</th>
                        <td>{item.median_change_percent.toFixed(1)}%</td>
                        <td>{item.p90_change_percent.toFixed(1)}%</td>
                        <td>
                          {item.suggested_drop_percent
                            ? `${item.suggested_drop_percent.toFixed(0)}%`
                            : '—'}
                        </td>
                        <td>{item.expected_alerts_per_week ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="actions">
                <button type="button" className="button secondary" onClick={applySuggestions}>
                  Apply to matching watches
                </button>
              </div>
            </div>
          )}

          {watches.length === 0 ? (
            <div className="card">
              <p className="placeholder">
                No watches configured — the daily check has nothing to report.
              </p>
            </div>
          ) : (
            <ConfigList>
              {watches.map((watch, index) => {
                const chips: RowChip[] = []
                if (!watch.enabled) chips.push({ key: 'off', text: 'Off', tone: 'off' })
                if (watch.muted_until) {
                  chips.push({ key: 'muted', text: `Muted to ${watch.muted_until}`, tone: 'off' })
                }
                chips.push({
                  key: 'severity',
                  text: watch.severity,
                  tone:
                    watch.severity === 'critical'
                      ? 'critical'
                      : watch.severity === 'warning'
                        ? 'warning'
                        : 'neutral',
                })
                return (
                  <ConfigRow
                    key={index}
                    name={watch.label || 'Unnamed watch'}
                    code={watch.kind === 'metric' ? metricLabel(watch.target) : watch.target}
                    summary={summariseWatch(watch)}
                    chips={chips}
                    dimmed={!watch.enabled}
                    active={editing === index}
                    onOpen={() => setEditing(index)}
                  />
                )
              })}
            </ConfigList>
          )}

          <Drawer
            open={open !== undefined}
            title={open?.label || 'Unnamed watch'}
            subtitle={
              open ? (open.kind === 'metric' ? metricLabel(open.target) : open.target) : undefined
            }
            onClose={() => setEditing(null)}
            footer={
              <>
                <button
                  type="button"
                  className="button danger"
                  onClick={() => {
                    setWatches(watches.filter((_, position) => position !== editing))
                    setEditing(null)
                    setDirty(true)
                  }}
                >
                  Remove
                </button>
                <button type="button" className="button" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save watches'}
                </button>
              </>
            }
          >
            {open !== undefined && editing !== null && (
              <WatchEditor watch={open} onChange={(patch) => update(editing, patch)} />
            )}
          </Drawer>
        </>
      )}
    </>
  )
}

interface EditorProps {
  watch: Watch
  onChange: (patch: Partial<Watch>) => void
}

/** Empty string means "no rule", which is different from a rule set to zero. */
function numberOrNull(raw: string): number | null {
  if (raw.trim() === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

function WatchEditor({ watch, onChange }: EditorProps) {
  return (
    <>
      <div className="drawer__grid">
        <label className="field">
          <span className="field__label">Label</span>
          <input
            value={watch.label}
            placeholder="Paywall views"
            maxLength={80}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Type</span>
          <select
            value={watch.kind}
            onChange={(event) => {
              const kind = event.target.value as Watch['kind']
              onChange({ kind, target: kind === 'metric' ? 'sessions' : '' })
            }}
          >
            <option value="metric">Metric</option>
            <option value="event">GA4 event</option>
          </select>
        </label>

        <label className="field">
          <span className="field__label">{watch.kind === 'metric' ? 'Metric' : 'Event name'}</span>
          {watch.kind === 'metric' ? (
            <select
              value={watch.target}
              onChange={(event) => onChange({ target: event.target.value })}
            >
              {WATCHABLE_METRICS.map((metric) => (
                <option key={metric} value={metric}>
                  {metricLabel(metric)}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={watch.target}
              placeholder="paywall_view"
              maxLength={100}
              spellCheck={false}
              onChange={(event) => onChange({ target: event.target.value })}
            />
          )}
        </label>

        <label className="field">
          <span className="field__label">Channel</span>
          <input
            value={watch.segment ?? ''}
            placeholder="All channels"
            onChange={(event) => onChange({ segment: event.target.value.trim() || null })}
          />
        </label>
      </div>

      <p className="drawer__section">Rules</p>

      <div className="drawer__grid">
        <label className="field">
          <span className="field__label">Alert if it falls %</span>
          <input
            type="number"
            min={1}
            max={100}
            value={watch.drop_percent ?? ''}
            placeholder="off"
            onChange={(event) => onChange({ drop_percent: numberOrNull(event.target.value) })}
          />
        </label>

        <label className="field">
          <span className="field__label">Alert if it rises %</span>
          <input
            type="number"
            min={1}
            value={watch.spike_percent ?? ''}
            placeholder="off"
            onChange={(event) => onChange({ spike_percent: numberOrNull(event.target.value) })}
          />
        </label>

        <label className="field">
          <span className="field__label">Alert if below</span>
          <input
            type="number"
            min={0}
            value={watch.minimum ?? ''}
            placeholder="off"
            onChange={(event) => onChange({ minimum: numberOrNull(event.target.value) })}
          />
        </label>

        <label className="field">
          <span className="field__label">Severity</span>
          <select
            value={watch.severity}
            onChange={(event) => onChange({ severity: event.target.value as Watch['severity'] })}
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </label>

        <label className="field">
          <span className="field__label">Mute until</span>
          <input
            type="date"
            value={watch.muted_until ?? ''}
            onChange={(event) => onChange({ muted_until: event.target.value || null })}
          />
        </label>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={watch.enabled}
          onChange={(event) => onChange({ enabled: event.target.checked })}
        />
        Enabled
      </label>
    </>
  )
}
